// Jen typy: runtime schémata žijí v `schemas/media.ts` (řez 13, Kontrakt 4(a)), takže
// `types/**` za běhu na zod nedosáhne. `import type` se při překladu vymaže.
import type { z } from "zod";
import type { FocalSource } from "../media/focal";
import type {
  AdminMediaSchema,
  AiRefreshSchema,
  AssignCountriesSchema,
  MediaBulkActionSchema,
  MediaCreateSchema,
  MediaFolderSchema,
  MediaHashCheckSchema,
  MediaListQuerySchema,
  MediaSchema,
  MediaUpdateSchema,
} from "../schemas/media";

// Folders mirror media.folder enum in packages/db/src/schema/media.ts
export const MEDIA_FOLDERS = [
  "destinations",
  "trips",
  "activities",
  "guides",
  "blog",
  "teams",
  "general",
  "departure-photos",
] as const;

export type MediaFolder = z.infer<typeof MediaFolderSchema>;

export type MediaResponse = z.infer<typeof MediaSchema>;
export type MediaCreate = z.infer<typeof MediaCreateSchema>;
export type MediaUpdate = z.infer<typeof MediaUpdateSchema>;

/** AI metadata suggestion result (POST /api/admin/upload/suggest-alt). */
export interface AltSuggestion {
  alt: string;
  title: string;
  tags: string[];
  description: string;
}

// ── Knihovna médií (vize podnety-tymu-2026-10, řez 03) ──

/** Médium v adminu: `MediaResponse` + zdroj, ★, země, počet použití, živé dvojče v koši. */
export type AdminMediaResponse = z.infer<typeof AdminMediaSchema>;
export type MediaListQuery = z.infer<typeof MediaListQuerySchema>;
export type MediaBulkAction = z.infer<typeof MediaBulkActionSchema>;

/** Odpověď `GET /api/admin/upload` (uvnitř `ApiResponse`). */
export interface MediaListResponse {
  data: AdminMediaResponse[];
  nextCursor: string | null;
  hasMore: boolean;
  /** Počet fotek podle TÝCHŽ filtrů (ne počet načtených). */
  total: number;
}

/**
 * Druhy míst použití fotky (`MediaUsage.kind`). `"user"` = avatar uživatele (`users.avatar`,
 * stará adresa s klíčem R2); `label` = jméno uživatele, `slug: null`, v detailu fotky bez
 * odkazu na entitu.
 */
export const MEDIA_USAGE_KINDS = [
  "destination",
  "trip",
  "inspiration",
  "blogPost",
  "contentPage",
  "guide",
  "destinationTeam",
  "premiumUpgrade",
  "review",
  "departurePhoto",
  "jobOffer",
  "newsletterCampaign",
  "setting",
  "user",
] as const;
export type MediaUsageKind = (typeof MEDIA_USAGE_KINDS)[number];

/** Jeden výskyt odkazu na fotku v řádku databáze (index `apps/api/src/lib/mediaUsage.ts`). */
export interface MediaUsage {
  kind: MediaUsageKind;
  /** ID řádku; u nastavení klíč v `settings`. */
  entityId: string;
  /** Název nebo titul entity; u nastavení klíč. */
  label: string;
  slug: string | null;
  /** Vlastnost Drizzle, u JSON s cestou: `"hotelRooms[].imageId"`. */
  field: string;
  /** Jen u `trip`: zájezd je hotel (`isHotelTrip`). */
  isHotel?: boolean;
  /** Odkaz ID média, nebo klíčem R2 v textu / starém sloupci s adresou. */
  via: "id" | "r2Key";
}

/** Odpověď `GET /api/admin/upload/:id/usage` (uvnitř `ApiResponse`). */
export interface MediaUsageResponse {
  usages: MediaUsage[];
  /** = `usages.length` */
  count: number;
  // Dnešní klíče (čte je i scripts/import-old-web/nasazeni_metadat.py):
  destinations: Array<{ id: string; slug: string; name: string }>;
  trips: Array<{ id: string; slug: string; title: string }>;
  blogPosts: Array<{ id: string; slug: string; title: string }>;
  activities: Array<{ id: string; slug: string; title: string }>;
  guides: Array<{ id: string; slug: string; name: string }>;
}

/** Odpověď `GET /api/admin/upload/sources`: dřívější zdroje, česky abecedně (`sortCzech`). */
export interface MediaSourcesResponse {
  sources: string[];
}

// ── Nástroje knihovny (`/api/admin/backfill-media/*`, řez 08) ──

/** Jedna skupina přesných duplicit v náhledu sloučení. */
export interface MergeGroupPreview {
  hash: string;
  canonicalId: string;
  canonicalAlt: string | null;
  dupIds: string[];
}

/** Odpověď `POST /api/admin/backfill-media/merge-duplicates` (uvnitř `ApiResponse`). */
export interface MergeDuplicatesResponse {
  dry: boolean;
  /** Skupiny v rozsahu požadavku (bez `hashes` celá knihovna). */
  groups: number;
  /** Kopie do koše. */
  duplicates: number;
  /** = `touched` z `remapAllMediaRefsDetailed`. */
  entitiesTouched: number;
  /** Bez `hashes` nejvýš `MERGE_BATCH_MAX_HASHES` skupin, s `hashes` všechny. */
  preview: MergeGroupPreview[];
  /** Otisky všech skupin rozsahu, seřazené vzestupně (dialog z nich skládá dávky). */
  hashes: string[];
  /** Jen s `hashes`: přepsané řádky (`settings` s klíčem jako `id`). */
  touchedRows?: Array<{ table: string; id: string }>;
  /** Jen s `hashes`: ID kanonických, které převzaly ohnisko kopie. */
  focalAdopted?: string[];
  /** Jen s `hashes`: otisky bez skupiny (už sloučené, nebo jedna živá). */
  hashesWithoutGroup?: string[];
}

/**
 * Jedna fotka v odpovědi `assign-countries`. `countryIds` = země podle použití (výpočet, ne
 * stav po zápisu), `current` = země fotky před zápisem požadavku; obojí seřazené. Fotka
 * v koši nebo neexistující v odpovědi není.
 */
export interface AssignCountriesItem {
  id: string;
  countryIds: string[];
  current: string[];
}

/** Odpověď `POST /api/admin/backfill-media/assign-countries` (uvnitř `ApiResponse`). */
export interface AssignCountriesResponse {
  items: AssignCountriesItem[];
  /** Počet fotek, které dostaly aspoň jednu novou zemi (ne počet řádků `media_countries`; při `dry` 0). */
  written: number;
}

export type AssignCountriesBody = z.input<typeof AssignCountriesSchema>;

// ── Opakování AI nad ID (`/api/admin/backfill-media/ai-refresh`, řez 09) ──

export type AiRefreshBody = z.input<typeof AiRefreshSchema>;

/** Stav ohniska fotky: účinný bod, režim a AI bod (`null` = AI bod nemá). */
export interface FocalState {
  x: number;
  y: number;
  source: FocalSource;
  aiX: number | null;
  aiY: number | null;
}

/**
 * Stav položky. Při `focal: true` popisuje ohnisko: `skipped-manual` (ruční fotka, AI se pro
 * bod nevolá), `unchanged` (fotka AI bod už má, nic se nezapíše), `updated`, `ai-failed` (nic
 * se nezapíše). Při `focal: false` podle textů: `regenerated` → `updated`, `present` →
 * `unchanged`, `failed` → `ai-failed`. `not-found` = ID neexistuje nebo je v koši
 * (`focal` i `texts` `null`, nic se nezapíše). Výčet čte i `scripts/media/ai-ohnisko.mjs` přes
 * `nactiTs` (Node jen odstraňuje typy), proto soubor drží jen vymazatelnou syntaxi TS.
 */
export const AI_REFRESH_STATUSES = [
  "updated",
  "unchanged",
  "skipped-manual",
  "ai-failed",
  "not-found",
] as const;
export type AiRefreshStatus = (typeof AI_REFRESH_STATUSES)[number];

/** Výsledek AI textů položky; `null` při `texts: false` nebo `not-found`. */
export type AiRefreshTexts = "regenerated" | "present" | "failed" | null;

export interface AiRefreshItem {
  id: string;
  status: AiRefreshStatus;
  /** Stav před a po (při `dry` po = co by zápis měl); `null` při `focal: false` nebo `not-found`. */
  focal: { before: FocalState; after: FocalState } | null;
  texts: AiRefreshTexts;
}

/** Odpověď `POST /api/admin/backfill-media/ai-refresh` (uvnitř `ApiResponse`). */
export interface AiRefreshResponse {
  items: AiRefreshItem[];
  /**
   * Počet položek, jejichž řádek `media` zápis změnil (`status: "updated"` nebo
   * `texts: "regenerated"`; náhled se zvlášť nepočítá); při `dry` 0.
   */
  written: number;
}

// ── Hromadné nahrávání (`/api/admin/upload`, řez 04) ──

export type MediaHashCheckBody = z.infer<typeof MediaHashCheckSchema>;

/**
 * Jedna položka odpovědi `check-hashes` na vstupní otisk, ve stejném pořadí. Shoda je
 * s `contentHash` NEBO `originalContentHash`. `MediaResponse` je z čisté `toMediaResponse`
 * (bez dotazů do indexu použití); panel čte `id`, `thumbUrl` a `deletedAt`.
 */
export interface MediaHashMatch {
  hash: string;
  /** Živá fotka; při více nejstarší podle `uploadedAt`, pak `id`. */
  live: MediaResponse | null;
  /** Fotka v koši; vyplní se jen, když `live` je `null`. */
  trashed: MediaResponse | null;
}

/** Odpověď `POST /api/admin/upload/check-hashes` (uvnitř `ApiResponse`). */
export interface MediaHashCheckResponse {
  matches: MediaHashMatch[];
}

/**
 * Odpověď `POST /api/admin/upload` a položky `uploaded` v `POST /multiple` (sync).
 * `reused: true` = vrácená existující fotka (HTTP 200), `false` = nová (201). Otisky
 * odpověď nenese; čte je `GET /api/admin/upload/:id` (`AdminMediaResponse`).
 */
export type MediaUploadResponse = MediaResponse & { reused: boolean };
